import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AttachmentTypeActions } from './attachment-types.actions';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import {
    GET_ALL_ATTACHMENT_TYPES,
    SAVE_ATTACHMENT_TYPE,
    DELETE_ATTACHMENT_TYPE
} from '@modules/settings/settings.graphql';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
    GetAllAttachmentTypesResponse,
    SaveAttachmentTypeResponse
} from '@modules/settings/types/attachment-type';

@Injectable()
export class AttachmentTypeEffects {
    private readonly actions$ = inject(Actions);
    private readonly graphqlService = inject(GraphQlService);
    private readonly swalService = inject(SwalService);

    /**
     * Load All Attachment Types
     */
    loadAll$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttachmentTypeActions.loadAll),
            switchMap(() =>
                this.graphqlService.query<GetAllAttachmentTypesResponse>({
                    query: GET_ALL_ATTACHMENT_TYPES,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { first: 0, size: 1000 } // Fetching all for now
                }).pipe(
                    map((response) =>
                        AttachmentTypeActions.loadAllSuccess({
                            attachmentTypes: response.data!.getAllAttachmentTypes.content
                        })
                    ),
                    catchError((error) =>
                        of(AttachmentTypeActions.loadAllFailure({ error }))
                    )
                )
            )
        )
    );

    /**
     * Create Attachment Type
     */
    create$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttachmentTypeActions.create),
            mergeMap(({ input }) =>
                this.graphqlService.mutate<{ saveAttachmentType: SaveAttachmentTypeResponse }>({
                    mutation: SAVE_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { input }
                }).pipe(
                    map((response) => {
                        const res = response.data!.saveAttachmentType;
                        if (res.status) {
                            return AttachmentTypeActions.createSuccess({ entity: res.data });
                        } else {
                            return AttachmentTypeActions.createFailure({ error: res.message });
                        }
                    }),
                    catchError((error) =>
                        of(AttachmentTypeActions.createFailure({ error }))
                    )
                )
            )
        )
    );

    /**
     * Update Attachment Type
     */
    update$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttachmentTypeActions.update),
            mergeMap(({ uuid, changes }) => {
                const input = { uuid, ...changes };
                return this.graphqlService.mutate<{ saveAttachmentType: SaveAttachmentTypeResponse }>({
                    mutation: SAVE_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { input }
                }).pipe(
                    map((response) => {
                        const res = response.data!.saveAttachmentType;
                        if (res.status) {
                            return AttachmentTypeActions.updateSuccess({ entity: res.data });
                        } else {
                            return AttachmentTypeActions.updateFailure({ error: res.message });
                        }
                    }),
                    catchError((error) =>
                        of(AttachmentTypeActions.updateFailure({ error }))
                    )
                );
            })
        )
    );

    /**
     * Delete Attachment Type
     */
    delete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AttachmentTypeActions.delete),
            mergeMap(({ uuid }) =>
                this.graphqlService.mutate({
                    mutation: DELETE_ATTACHMENT_TYPE,
                    apolloNamespace: ApolloNamespace.registration,
                    variables: { uuid }
                }).pipe(
                    map(() =>
                        AttachmentTypeActions.deleteSuccess({ uuid })
                    ),
                    catchError((error) =>
                        of(AttachmentTypeActions.deleteFailure({ error }))
                    )
                )
            )
        )
    );

    /**
     * Show Notifications
     */
    notifications$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    AttachmentTypeActions.createSuccess,
                    AttachmentTypeActions.updateSuccess,
                    AttachmentTypeActions.deleteSuccess
                ),
                tap((action) => {
                    if (action.type === AttachmentTypeActions.createSuccess.type) {
                        this.swalService.successToast('Attachment Type Created Successfully');
                    } else if (action.type === AttachmentTypeActions.updateSuccess.type) {
                        this.swalService.successToast('Attachment Type Updated Successfully');
                    } else if (action.type === AttachmentTypeActions.deleteSuccess.type) {
                        this.swalService.successToast('Attachment Type Deleted Successfully');
                    }
                })
            ),
        { dispatch: false }
    );
}
